<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    id: string
    modelValue?: string | number
    label?: string
    type?: 'text' | 'number' | 'money'
    placeholder?: string
    hint?: string
    unit?: string
    min?: number
    max?: number
    step?: number | string
    required?: boolean
    error?: string
    ariaDescribedby?: string
  }>(),
  {
    type: 'text',
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'blur'): void
}>()

const inputType = computed(() => (props.type === 'money' ? 'number' : props.type))

// Numeric keypad on iOS/Android for money and number fields.
const inputMode = computed(() =>
  props.type === 'money' || props.type === 'number' ? 'decimal' : undefined
)

function onInput(event: Event) {
  const target = event.target as HTMLInputElement
  emit('update:modelValue', target.value)
}
</script>

<template>
  <div class="field">
    <label v-if="label" :for="id" class="field__label">
      {{ label }}
      <span v-if="required" class="field__required" aria-hidden="true">*</span>
    </label>
    <div class="field__wrapper">
      <input
        :id="id"
        :type="inputType"
        :inputmode="inputMode"
        :value="modelValue"
        :placeholder="placeholder"
        :min="min"
        :max="max"
        :step="step"
        :required="required"
        :aria-describedby="ariaDescribedby"
        :aria-invalid="!!error"
        class="field__input"
        :class="{ 'field__input--error': error }"
        @input="onInput"
        @blur="$emit('blur')"
      />
      <span v-if="unit" class="field__unit" aria-hidden="true">{{ unit }}</span>
    </div>
    <p v-if="hint && !error" :id="`${id}-hint`" class="field__hint">{{ hint }}</p>
    <p v-if="error" :id="`${id}-error`" class="field__error" role="alert">{{ error }}</p>
  </div>
</template>

<style scoped>
.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.field__label {
  font-weight: var(--font-medium);
  font-size: var(--text-sm);
  color: var(--color-text);
}

.field__required {
  color: var(--color-error);
}

.field__wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.field__input {
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-md);
  background-color: var(--color-surface);
  color: var(--color-text);
  font-size: var(--text-base);
  line-height: var(--leading-normal);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;
  appearance: textfield;
}

.field__input::-webkit-outer-spin-button,
.field__input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.field__input:focus-visible {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgb(10 92 86 / 0.15);
}

.field__input--error {
  border-color: var(--color-error);
}

.field__unit {
  position: absolute;
  right: var(--space-3);
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  pointer-events: none;
}

.field__hint,
.field__error {
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
}

.field__hint {
  color: var(--color-text-muted);
}

.field__error {
  color: var(--color-error);
}
</style>
